"use client";

import React, { useMemo, useState, Suspense } from "react";
import { Eye, EyeOff, Lock, Mail, Building2, AtSign, ArrowRight } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { createBrowserClient } from "@/lib/supabase/client";
import { AuthShell } from "@/components/auth/AuthShell";
import { AuthPanel } from "@/components/auth/AuthPanel";
import { AuthTabs } from "@/components/auth/AuthTabs";
import { AuthField } from "@/components/auth/AuthField";
import { PrimaryButton, SocialButton, Divider } from "@/components/auth/AuthButtons";
import { isTenantSlugRequired } from "@/lib/auth/config";
import Link from "next/link";

function cn(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

type TabType = "password" | "magic-link" | "sso";

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>("password");
  const [tenantSlug, setTenantSlug] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState<null | { type: "ok" | "err"; msg: string }>(
    null
  );

  const requireTenantSlug = isTenantSlugRequired();

  // Get redirectTo from query string, validate it's an internal path
  const redirectTo = useMemo(() => {
    const rawRedirectTo = searchParams?.get("redirectTo");
    // Only allow internal redirects (must start with "/")
    if (rawRedirectTo && rawRedirectTo.startsWith("/")) {
      return rawRedirectTo;
    }
    return null;
  }, [searchParams]);

  const validEmail = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()),
    [email]
  );
  const validSlug = useMemo(
    () => !requireTenantSlug || tenantSlug.trim().length >= 2,
    [requireTenantSlug, tenantSlug]
  );
  const canSubmitPassword = validEmail && password.length >= 1 && validSlug;
  const canSubmitMagicLink = validEmail;

  const handlePasswordSignIn = async () => {
    setBanner(null);
    
    // Validate inputs
    if (!email.trim()) {
      setBanner({ type: "err", msg: "Please enter your email address." });
      return;
    }
    
    if (!validEmail) {
      setBanner({ type: "err", msg: "Please enter a valid email address." });
      return;
    }
    
    if (!password) {
      setBanner({ type: "err", msg: "Please enter your password." });
      return;
    }
    
    if (requireTenantSlug && !tenantSlug.trim()) {
      setBanner({ type: "err", msg: "Please enter your tenant/company slug." });
      return;
    }
    
    if (requireTenantSlug && tenantSlug.trim().length < 2) {
      setBanner({ type: "err", msg: "Tenant slug must be at least 2 characters." });
      return;
    }
    
    setLoading(true);

    try {
      const supabase = createBrowserClient();
      if (!supabase) {
        throw new Error("Supabase client not configured");
      }

      // Step 1: Always authenticate with Supabase using email + password first
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password: password,
      });

      if (error) {
        throw new Error(error.message || "Invalid email or password");
      }

      if (!data.user) {
        throw new Error("Login failed");
      }

      // Step 2: Handle redirect after successful login
      // Use redirectTo if provided and valid, otherwise use default tenant selection flow
      if (redirectTo) {
        // User has a specific redirect path (e.g., /invite/<token>/complete)
        router.replace(redirectTo);
      } else if (requireTenantSlug && tenantSlug.trim()) {
        // If tenant slug is required, resolve tenant by slug
        // The select-tenant page or middleware will handle tenant resolution
        // For now, redirect to select-tenant which will handle tenant lookup
        router.push(`/select-tenant?slug=${encodeURIComponent(tenantSlug.trim())}`);
      } else {
        // If tenant slug is not required, resolve tenant from DB using user_id
        // Redirect to select-tenant which will automatically resolve tenant from user memberships
        router.push("/select-tenant");
      }
    } catch (error: any) {
      // Guard: Expected 400/401 errors are normal login failures, not critical errors
      const errorCode = error?.status || error?.code || error?.statusCode
      if (errorCode === 400 || errorCode === 401) {
        // Expected error - user entered wrong credentials, suppress from console spam
        // Error message is already shown to user via setBanner()
        if (process.env.NODE_ENV === 'development') {
          console.warn('[Auth] Login failed (expected)', errorCode)
        }
      } else {
        // Unexpected error - log for debugging
        console.error("Login error:", error);
      }
      setBanner({
        type: "err",
        msg: error.message || "Failed to sign in. Please try again.",
      });
      setLoading(false);
    }
  };

  const handleMagicLink = async () => {
    setBanner(null);
    if (!canSubmitMagicLink) {
      setBanner({ type: "err", msg: "Enter your email address." });
      return;
    }
    setLoading(true);

    try {
      // Use redirectTo if provided, otherwise default to /select-tenant
      const magicLinkRedirectTo = redirectTo || "/select-tenant";
      
      const response = await fetch("/api/auth/magic-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          redirectTo: magicLinkRedirectTo,
        }),
      });

      const result = await response.json();

      if (!result.ok) {
        throw new Error(result.message || "Failed to send magic link");
      }

      setBanner({
        type: "ok",
        msg: result.message || "Magic link sent! Check your email.",
      });
    } catch (error: any) {
      console.error("Magic link error:", error);
      setBanner({
        type: "err",
        msg: error.message || "Failed to send magic link. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSSO = async (provider: "google" | "microsoft") => {
    setBanner(null);
    setLoading(true);

    try {
      const supabase = createBrowserClient();
      if (!supabase) {
        throw new Error("Supabase client not configured");
      }

      // Build redirectTo for OAuth callback
      // Use redirectTo if provided, otherwise default to /select-tenant
      const oauthRedirectTo = redirectTo || "/select-tenant";
      
      // Get the current origin for the callback URL
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const callbackUrl = `${origin}/auth/callback?redirectTo=${encodeURIComponent(oauthRedirectTo)}`;

      // Map UI provider name to Supabase provider name
      // Supabase uses "azure" for Microsoft, not "microsoft"
      const supabaseProvider = provider === "microsoft" ? "azure" : provider;

      // Initiate OAuth sign-in
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: supabaseProvider as "google" | "azure",
        options: {
          redirectTo: callbackUrl,
        },
      });

      if (error) {
        throw new Error(error.message || `Failed to initiate ${provider} sign-in`);
      }

      // OAuth will redirect automatically, so we don't need to handle redirect here
      // The user will be redirected to the OAuth provider, then back to /auth/callback
      // which will then redirect to the redirectTo path
    } catch (error: any) {
      console.error(`${provider} SSO error:`, error);
      setBanner({
        type: "err",
        msg: error.message || `${provider === "google" ? "Google" : "Microsoft"} SSO is not available. Please try password login.`,
      });
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <AuthPanel>
        {/* Tabs */}
        <AuthTabs activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-lg font-semibold tracking-tight">Welcome back</div>
            <div className="mt-1 text-sm text-white/60">
              Sign in to your tenant workspace.
            </div>
          </div>
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-white/[0.06] ring-1 ring-white/10">
            <Lock className="h-5 w-5 text-white/70" />
          </div>
        </div>

        {/* Banner */}
        {banner ? (
          <div
            className={cn(
              "mt-5 rounded-xl border px-3 py-2 text-sm",
              banner.type === "ok"
                ? "border-emerald-400/20 bg-emerald-500/10 text-emerald-200"
                : "border-amber-400/20 bg-amber-500/10 text-amber-200"
            )}
          >
            {banner.msg}
          </div>
        ) : null}

        {/* Password Tab */}
        {activeTab === "password" && (
          <div className="mt-6 grid gap-4">
            {requireTenantSlug && (
              <AuthField
                label="Tenant / Company"
                placeholder="@ tenant-slug"
                value={tenantSlug}
                onChange={setTenantSlug}
                icon={<AtSign className="h-4 w-4" />}
                helperText="Example: bsms-security or ops-next-level"
                error={!validSlug && tenantSlug.length > 0 ? "Tenant slug must be at least 2 characters" : undefined}
              />
            )}

            <AuthField
              label="Email"
              placeholder="you@company.com"
              value={email}
              onChange={setEmail}
              type="email"
              autoComplete="email"
              icon={<Mail className="h-4 w-4" />}
              helperText="Use your work email"
            />

            <AuthField
              label="Password"
              placeholder="Enter your password"
              value={password}
              onChange={setPassword}
              type={showPw ? "text" : "password"}
              autoComplete="current-password"
              icon={<Lock className="h-4 w-4" />}
              helperText="Minimum 8 characters"
              right={
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="grid h-9 w-9 place-items-center rounded-lg text-white/55 hover:bg-white/[0.06] hover:text-white transition"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              }
            />

            <div className="flex items-center justify-between">
              <Link
                href="/auth/forgot-password"
                className="text-xs font-medium text-emerald-300 hover:text-emerald-200"
              >
                Forgot password?
              </Link>
              <Link
                href="/auth/accept-invite"
                className="text-xs font-medium text-emerald-300 hover:text-emerald-200"
              >
                Need access?
              </Link>
            </div>

            <PrimaryButton 
              disabled={!canSubmitPassword} 
              loading={loading} 
              onClick={handlePasswordSignIn}
              type="button"
            >
              <span className="flex items-center justify-center gap-2">
                Sign in
                <ArrowRight className="h-4 w-4" />
              </span>
            </PrimaryButton>
          </div>
        )}

        {/* Magic Link Tab */}
        {activeTab === "magic-link" && (
          <div className="mt-6 grid gap-4">
            <AuthField
              label="Email address"
              placeholder="name@company.com"
              value={email}
              onChange={setEmail}
              type="email"
              autoComplete="email"
              icon={<Mail className="h-4 w-4" />}
              helperText="We'll send you a secure link to sign in without a password."
            />

            <PrimaryButton disabled={!canSubmitMagicLink} loading={loading} onClick={handleMagicLink}>
              Send Magic Link
            </PrimaryButton>
          </div>
        )}

        {/* SSO Tab */}
        {activeTab === "sso" && (
          <div className="mt-6 grid gap-4">
            <div className="text-sm text-white/60">
              Sign in using your organization&apos;s SSO provider.
            </div>

            <SocialButton provider="google" onClick={() => handleSSO("google")} />
            <SocialButton provider="microsoft" onClick={() => handleSSO("microsoft")} />
          </div>
        )}

        {/* Divider and Social Buttons (only show on password tab) */}
        {activeTab === "password" && (
          <>
            <Divider text="or" />
            <SocialButton provider="google" onClick={() => handleSSO("google")} />
            <SocialButton provider="microsoft" onClick={() => handleSSO("microsoft")} />
          </>
        )}

        {/* Already have account section */}
        <div className="mt-6 flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
          <div className="text-sm text-white/70">
            New here?
          </div>
          <Link
            href="/auth/signup"
            className={cn(
              "rounded-lg px-4 py-2 text-sm font-semibold",
              "bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-400/20",
              "hover:bg-emerald-500/15 hover:text-emerald-200 transition"
            )}
          >
            Create an account
          </Link>
        </div>
      </AuthPanel>
    </AuthShell>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <AuthShell>
        <AuthPanel>
          <div className="flex items-center justify-center py-12">
            <div className="text-sm text-white/60">Loading...</div>
          </div>
        </AuthPanel>
      </AuthShell>
    }>
      <LoginForm />
    </Suspense>
  );
}
